import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useResponsive } from '../../hook/useResponsive';
import { getProductPerformanceByDate } from '../../services/productPerformance.service';
import { getListProduct } from '../../services/items.services';
import { showApiError } from '../../services/apiError';
import { DropdownSearch } from '../../components/DropdownSearch';
import { FilterField, IconCalendar, filterControlStyle } from '../../components/FilterField';
import { useDebounceEffect } from '../../hook/useDebounceEffect';
import {
  Density,
  ListColumn,
  ListFilterBar,
  ListMobile,
  ListTable,
  SortDir,
  TEXT,
  TEXT_MUTED,
  cells,
  formatDayMonth,
  formatMoney,
  t,
  toLocalDay,
} from '../../components/list';

/** One report line, keyed client-side (one per day × product). */
interface PerformanceRow {
  key: string;
  /** Calendar day as YYYY-MM-DD text in the venue's zone — never a Date. */
  txDate: string;
  sku: string;
  product: string;
  quantity: number;
  refundedQuantity: number;
  net: number;
  tax: number;
  gross: number;
}

interface Totals {
  quantity: number;
  refundedQuantity: number;
  net: number;
  tax: number;
  gross: number;
}

const EMPTY_TOTALS: Totals = { quantity: 0, refundedQuantity: 0, net: 0, tax: 0, gross: 0 };

// Filter bar styling is inline on purpose: the host compiles Tailwind from its
// own sources only, so utility classes used just here never reach its CSS.
const fieldStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 };
const labelStyle: React.CSSProperties = { fontSize: 12, color: '#4A5353', fontWeight: 500, letterSpacing: '0.02em' };
const inputStyle: React.CSSProperties = {
  height: 44,
  width: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  borderRadius: 10,
  border: '1px solid #DBE4E4',
  padding: '0 12px',
  background: '#FFFFFF',
  color: '#222525',
  fontSize: 14,
  fontFamily: 'inherit',
};

/** The API caps a request at 366 calendar days (dateTo - dateFrom + 1). */
const MAX_RANGE_DAYS = 366;
/** The API returns at most this many (day, product) rows and flags `truncated`. */
const ROW_CAP = 5000;

const num = (v: any): number => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/** Inclusive calendar days between two YYYY-MM-DD strings (no DST arithmetic). */
const inclusiveDays = (from: string, to: string): number => {
  const [y1, m1, d1] = from.split('-').map(Number);
  const [y2, m2, d2] = to.split('-').map(Number);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86400000) + 1;
};

/** "5 Aug" from 'YYYY-MM-DD' text: build the Date from its parts, never from the ISO string. */
const formatDisplayDate = (val: string): string => {
  const [y, m, d] = String(val ?? '').slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return val ? String(val) : ' - ';
  return formatDayMonth(new Date(y, m - 1, d));
};

/** "1,234.5" — quantities carry up to three decimals; drop trailing zeros. */
const formatQty = (v: number): string =>
  v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 3 });

const ProductPerformance = () => {
  const [language, setLanguage] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const { isMobile } = useResponsive();
  const companyId = localStorage.getItem('companyId');
  const locationId = localStorage.getItem('locationId');

  const defaultFrom = new Date();
  defaultFrom.setDate(1);
  defaultFrom.setMonth(defaultFrom.getMonth() - 1);
  const defaultTo = new Date();
  // Local calendar day (toISOString() shifted +UTC users back a day).
  const toYyyyMmDd = (d: Date) => toLocalDay(d);

  const [dateFrom, setDateFrom] = useState<string>(toYyyyMmDd(defaultFrom));
  const [dateTo, setDateTo] = useState<string>(toYyyyMmDd(defaultTo));
  const [productId, setProductId] = useState<string>('');
  const [products, setProducts] = useState<{ id: string; productName: string; sku?: string }[]>([]);
  const [tableData, setTableData] = useState<PerformanceRow[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [truncated, setTruncated] = useState(false);
  // Sorting is client-side: the endpoint has no sortBy, rows arrive gross DESC.
  const [sortBy, setSortBy] = useState<string>('gross');
  const [sortDir, setSortDir] = useState<SortDir>('DESC');
  // The report is fetched whole; the kit pages it in memory.
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(50);
  const [density, setDensity] = useState<Density>('default');
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({});
  
  // DropdownSearch state
  const [searchProduct, setSearchProduct] = useState<string>('');
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState<boolean>(false);
  const dropdownProductRef = useRef<HTMLDivElement>(null);
  const [filterProduct, setFilterProduct] = useState({ page: 1, searchValue: '', limit: 50 });
  const [hasMoreProduct, setHasMoreProduct] = useState<boolean>(true);
  const [loadingProduct, setLoadingProduct] = useState<boolean>(false);
  const PAGE_SIZE = 50;

  // Before the first Load the table explains how to start; after an empty
  // successful load it says there were no sales (an API failure toasts instead).
  const inforTable: any = hasSearched
    ? {
        title: t(language, 'No sales in this range', 'No sales in this range'),
        path: '/productPerformance',
        desc: t(
          language,
          'Try a wider date range or a different product.',
          'Try a wider date range or a different product.'
        ),
      }
    : {
        title: t(language, 'No Product Performance data', 'No Product Performance data'),
        path: '/productPerformance',
        desc: t(
          language,
          'Select date range and optionally a product, then click Load.',
          'Select date range and optionally a product, then click Load.'
        ),
      };

  useEffect(() => {
    const refresh = () => {
      if (window.languageContext) {
        setLanguage(window.languageContext.language);
      }
    };
    refresh();
    // The host loads translations asynchronously and announces them; without
    // this a cold load in Albanian stays English until a remount.
    document.addEventListener('languageLoaded', refresh);
    return () => document.removeEventListener('languageLoaded', refresh);
  }, []);

  // Load products with pagination and search
  const fetchProducts = useCallback(async (filter: { page: number; searchValue: string; limit: number }, isAppend: boolean = false) => {
    if (!companyId) return;
    try {
      setLoadingProduct(true);
      const res: any = await getListProduct(filter.page, filter.limit, filter.searchValue, companyId);
      const list = res?.data?.data?.products ?? res?.data?.products ?? [];
      
      // Filter out deleted products and sort alphabetically by productName
      const filteredAndSorted = Array.isArray(list)
        ? [...list]
            .filter((item) => !item.isDeleted)
            .sort((a, b) => {
              const nameA = (a.productName || '').toLowerCase();
              const nameB = (b.productName || '').toLowerCase();
              return nameA.localeCompare(nameB);
            })
        : [];
      
      if (isAppend) {
        setProducts((prev) => {
          // Avoid duplicates when appending
          const existingIds = new Set(prev.map((p) => p.id));
          const newProducts = filteredAndSorted.filter((p) => !existingIds.has(p.id));
          return [...prev, ...newProducts];
        });
      } else {
        setProducts(filteredAndSorted);
      }
      // Use raw list length for pagination: full page means more may exist (filtering can reduce visible count)
      setHasMoreProduct(list.length >= filter.limit);
      setFilterProduct({
        page: filter.page + 1,
        searchValue: filter.searchValue,
        limit: filter.limit,
      });
    } catch (e) {
      console.error('Error loading products:', e);
      if (!isAppend) {
        setProducts([]);
      }
    } finally {
      setLoadingProduct(false);
    }
  }, [companyId]);

  // Initial load
  useEffect(() => {
    if (companyId) {
      fetchProducts({ page: 1, searchValue: '', limit: PAGE_SIZE }, false);
    }
  }, [companyId, fetchProducts]);

  // Handle product search
  const handleProductSearch = useCallback(async (value: string) => {
    setFilterProduct({ page: 1, searchValue: value, limit: PAGE_SIZE });
    await fetchProducts({ page: 1, searchValue: value, limit: PAGE_SIZE }, false);
  }, [fetchProducts]);

  // Debounced search - calls API as user types. Include fetchProducts so companyId changes use current closure.
  useDebounceEffect(
    () => {
      if (isProductDropdownOpen && searchProduct && searchProduct.trim()) {
        handleProductSearch(searchProduct.trim());
      } else if (isProductDropdownOpen && !searchProduct) {
        // Reset to initial load when search is cleared
        fetchProducts({ page: 1, searchValue: '', limit: PAGE_SIZE }, false);
      }
    },
    [searchProduct, isProductDropdownOpen, handleProductSearch, fetchProducts],
    500
  );

  // Load more products
  const onLoadMoreProduct = useCallback(async () => {
    await fetchProducts(filterProduct, true);
  }, [filterProduct, fetchProducts]);

  // Handle product selection
  const handleProductSelect = (product: any) => {
    setProductId(product.id);
    const displayName = product.sku ? `${product.sku} - ${product.productName}` : product.productName;
    setSearchProduct(displayName);
    setIsProductDropdownOpen(false);
  };

  // Mirrors the API's checks so a bad range never leaves the page.
  const rangeError = useMemo<string | null>(() => {
    if (!dateFrom || !dateTo) return null;
    if (dateFrom > dateTo) {
      return t(language, 'From date must be before To date', 'From date must be before To date');
    }
    if (inclusiveDays(dateFrom, dateTo) > MAX_RANGE_DAYS) {
      return t(
        language,
        'Date range cannot exceed 366 days',
        `Date range cannot exceed ${MAX_RANGE_DAYS} days`
      );
    }
    return null;
  }, [dateFrom, dateTo, language]);

  const canLoad = !loading && !!companyId && !!locationId && !!dateFrom && !!dateTo && !rangeError;

  const handleLoad = async () => {
    if (!canLoad) {
      return;
    }
    setLoading(true);
    try {
      const response = await getProductPerformanceByDate(
        companyId!,
        locationId!,
        dateFrom.trim(),
        dateTo.trim(),
        productId?.trim() || undefined,
        // Day bucketing happens in the browser's zone, as SalesOverview sends it.
        Intl.DateTimeFormat().resolvedOptions().timeZone
      );
      const raw: any = response?.data?.data ?? [];
      const rows = Array.isArray(raw) ? raw : [];
      const mapped: PerformanceRow[] = rows.map((row: any, index: number) => {
        const txDate = String(row.tx_date ?? '').slice(0, 10);
        const sku = row.SKU ?? row.sku ?? ' - ';
        const product = row.Product ?? row.product ?? ' - ';
        return {
          key: `${index}-${row.product_id ?? sku}-${txDate}`,
          txDate,
          sku,
          product,
          quantity: num(row.quantity),
          refundedQuantity: num(row.refunded_quantity),
          net: num(row.net),
          tax: num(row.tax),
          gross: num(row.gross ?? row.total_amount),
        };
      });
      setTableData(mapped);
      setTruncated(response?.data?.truncated === true);
      setPage(1);
      setHasSearched(true);
    } catch (error) {
      console.error('Error loading product performance:', error);
      showApiError(error, {
        language,
        titleFallback: t(language, 'Could not load product performance', 'Could not load product performance'),
      });
      // Keep whatever the user was looking at; the toast reports the failure.
      // Clearing here made a failed reload look like "no sales in this range".
      setTruncated(false);
    } finally {
      setLoading(false);
    }
  };

  // Grand totals over every loaded row — for "All products" as much as for one.
  const totals = useMemo<Totals>(
    () =>
      tableData.reduce(
        (acc, r) => ({
          quantity: acc.quantity + r.quantity,
          refundedQuantity: acc.refundedQuantity + r.refundedQuantity,
          net: acc.net + r.net,
          tax: acc.tax + r.tax,
          gross: acc.gross + r.gross,
        }),
        EMPTY_TOTALS
      ),
    [tableData]
  );

  const sortedRows = useMemo(() => {
    const dir = sortDir === 'ASC' ? 1 : -1;
    const by = (r: PerformanceRow): number | string =>
      sortBy === 'txDate' ? r.txDate : sortBy === 'quantity' ? r.quantity : r.gross;
    return [...tableData].sort((a, b) => {
      const va = by(a);
      const vb = by(b);
      if (va < vb) return -dir;
      if (va > vb) return dir;
      return a.txDate < b.txDate ? -1 : a.txDate > b.txDate ? 1 : 0;
    });
  }, [tableData, sortBy, sortDir]);

  const handleSortChange = (nextBy: string, nextDir: SortDir) => {
    setSortBy(nextBy);
    setSortDir(nextDir);
    setPage(1);
  };

  /* ---------------- List kit configuration ---------------- */

  const qtyCell = (get: (r: PerformanceRow) => number) => (r: PerformanceRow) => (
    <div
      className="font-inter"
      style={{ fontSize: '14px', color: TEXT, fontVariantNumeric: 'tabular-nums' }}
    >
      {formatQty(get(r))}
    </div>
  );

  const columns: ListColumn<PerformanceRow>[] = [
    {
      key: 'txDate',
      label: 'Date',
      width: 120,
      hideable: false,
      sortable: true,
      value: r => formatDisplayDate(r.txDate),
      mobile: 'subtitle',
    },
    {
      key: 'product',
      label: 'Product',
      width: 280,
      render: cells.primary(r => r.product, r => r.sku),
      value: r => r.product,
      mobile: 'title',
    },
    {
      key: 'quantity',
      label: 'Qty',
      width: 100,
      align: 'right',
      sortable: true,
      render: qtyCell(r => r.quantity),
      value: r => formatQty(r.quantity),
      mobile: 'sheet',
    },
    {
      key: 'refundedQuantity',
      label: 'Refunded',
      width: 100,
      align: 'right',
      render: qtyCell(r => r.refundedQuantity),
      value: r => formatQty(r.refundedQuantity),
      mobile: 'sheet',
    },
    {
      key: 'net',
      label: 'Net',
      width: 120,
      align: 'right',
      render: cells.money(r => r.net),
      value: r => formatMoney(r.net),
      mobile: 'sheet',
    },
    {
      key: 'tax',
      label: 'Tax',
      width: 110,
      align: 'right',
      render: cells.money(r => r.tax),
      value: r => formatMoney(r.tax),
      mobile: 'sheet',
    },
    {
      key: 'gross',
      label: 'Gross',
      width: 130,
      align: 'right',
      sortable: true,
      render: cells.money(r => r.gross, true),
      value: r => formatMoney(r.gross),
      mobile: 'trailing',
    },
  ];

  const totalsRow: Record<string, React.ReactNode> = {
    txDate: t(language, 'Totals', 'Totals'),
    quantity: formatQty(totals.quantity),
    refundedQuantity: formatQty(totals.refundedQuantity),
    net: formatMoney(totals.net),
    tax: formatMoney(totals.tax),
    gross: formatMoney(totals.gross),
  };

  const pageRows = sortedRows.slice((page - 1) * limit, (page - 1) * limit + limit);
  const rowKey = (r: PerformanceRow) => r.key;

  // Handle "All products" selection
  const handleClearProduct = () => {
    setProductId('');
    setSearchProduct('');
  };

  const showTotals = hasSearched && !loading && tableData.length > 0;

  const totalsCard = (
    <div
      className="font-inter"
      style={{
        padding: '12px 16px',
        borderRadius: '12px',
        border: '1px solid #EBF0F0',
        display: 'flex',
        flexWrap: 'wrap',
        gap: isMobile ? '12px 20px' : '12px 32px',
        width: isMobile ? '100%' : 'fit-content',
      }}
    >
      {(
        [
          ['Gross', formatMoney(totals.gross), true],
          ['Net', formatMoney(totals.net), false],
          ['Tax', formatMoney(totals.tax), false],
          ['Qty', formatQty(totals.quantity), false],
          ['Refunded', formatQty(totals.refundedQuantity), false],
        ] as [string, string, boolean][]
      ).map(([label, value, strong]) => (
        <div key={label} style={{ minWidth: isMobile ? '40%' : undefined }}>
          <p style={{ fontSize: '12px', color: TEXT_MUTED }}>{t(language, label, label)}</p>
          <p
            style={{
              fontSize: strong ? '20px' : '16px',
              fontWeight: 600,
              color: '#05292A',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {value}
          </p>
        </div>
      ))}
    </div>
  );

  const truncatedNotice = truncated && !loading && (
    <div
      role="status"
      className="font-inter"
      style={{
        marginTop: '12px',
        fontSize: '13px',
        color: '#92400E',
        background: '#FFFBEB',
        border: '1px solid #FDE68A',
        padding: '8px 12px',
        borderRadius: '8px',
      }}
    >
      {t(
        language,
        'Showing the first 5,000 rows — narrow the date range',
        `Showing the first ${ROW_CAP.toLocaleString('en-US')} rows — narrow the date range`
      )}
    </div>
  );

  return (
    <div
      className="h-full relative"
      style={{
        overflowY: isMobile ? 'auto' : undefined,
        paddingBottom: isMobile ? '24px' : undefined,
        display: isMobile ? undefined : 'flex',
        flexDirection: isMobile ? undefined : 'column',
      }}
    >
      <div
        style={{
          display: isMobile ? 'block' : 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 16,
        }}
      >
        <p className="font-inter" style={{ fontSize: '24px', fontWeight: '500', color: '#222525' }}>
          {language && language?.['Product Performance']
            ? language?.['Product Performance']
            : 'Product Performance'}
        </p>

        {/* Desktop: one row of From, To, Product, Load. Mobile: everything full width, the dates as the
            Sales Overview pills (two native date inputs side by side do not fit a phone). */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile
              ? 'minmax(0, 1fr) minmax(0, 1fr)'
              : 'minmax(150px, 190px) minmax(150px, 190px) minmax(240px, 320px) auto',
            gap: 12,
            alignItems: 'end',
            marginTop: isMobile ? 16 : 0,
          }}
        >
          {isMobile ? (
            <div style={{ gridColumn: '1 / span 2', display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
              <FilterField label={language?.['From'] ?? 'From'} icon={<IconCalendar />} fluid>
                <input
                  type="date"
                  value={dateFrom}
                  max={dateTo || undefined}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="font-inter"
                  style={filterControlStyle(true)}
                />
              </FilterField>
              <FilterField label={language?.['To'] ?? 'To'} icon={<IconCalendar />} fluid>
                <input
                  type="date"
                  value={dateTo}
                  min={dateFrom || undefined}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="font-inter"
                  style={filterControlStyle(true)}
                />
              </FilterField>
            </div>
          ) : (
            <>
              <div style={fieldStyle}>
                <label className="font-inter" style={labelStyle}>
                  {language?.['From'] ?? 'From'}
                </label>
                <input
                  type="date"
                  value={dateFrom}
                  max={dateTo || undefined}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="font-inter"
                  style={inputStyle}
                />
              </div>
              <div style={fieldStyle}>
                <label className="font-inter" style={labelStyle}>
                  {language?.['To'] ?? 'To'}
                </label>
                <input
                  type="date"
                  value={dateTo}
                  min={dateFrom || undefined}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="font-inter"
                  style={inputStyle}
                />
              </div>
            </>
          )}
          <div style={{ ...fieldStyle, gridColumn: isMobile ? '1 / span 2' : 'auto' }}>
            <label className="font-inter" style={labelStyle}>
              {language?.['Product'] ?? 'Product'}
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0, height: 44 }}>
                <DropdownSearch<{ id: string; productName: string; sku?: string }>
                  refKey="product"
                  refs={{ product: dropdownProductRef }}
                  openStates={{ product: isProductDropdownOpen }}
                  open={() => setIsProductDropdownOpen(true)}
                  close={() => setIsProductDropdownOpen(false)}
                  data={products}
                  labelExtractor={(item) => {
                    const name = item.productName || '';
                    const sku = item.sku || '';
                    return sku ? `${sku} - ${name}` : name;
                  }}
                  value={searchProduct}
                  setValue={setSearchProduct}
                  onSelect={handleProductSelect}
                  onSearch={handleProductSearch}
                  placeholder={language?.['All products'] ?? 'All products'}
                  tabIndex={0}
                  onLoadMore={onLoadMoreProduct}
                  hasMore={hasMoreProduct}
                  loading={loadingProduct}
                  borderNone={false}
                />
              </div>
              {productId && (
                <button
                  type="button"
                  onClick={handleClearProduct}
                  className="font-inter"
                  style={{
                    height: 44,
                    padding: '0 12px',
                    borderRadius: 10,
                    fontSize: 14,
                    backgroundColor: '#f3f4f6',
                    color: '#05292A',
                    border: '1px solid #DBE4E4',
                  }}
                  title={language?.['Clear'] ?? 'Clear'}
                >
                  {language?.['Clear'] ?? 'Clear'}
                </button>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={handleLoad}
            disabled={!canLoad}
            className="font-inter"
            style={{
              height: 44,
              padding: '0 20px',
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 500,
              color: '#FFFFFF',
              backgroundColor: !canLoad ? '#9ca3af' : '#05292A',
              gridColumn: isMobile ? '1 / span 2' : 'auto',
            }}
          >
            {loading
              ? (language?.['Loading...'] ?? 'Loading...')
              : (language?.['Load'] ?? 'Load')}
          </button>
        </div>
      </div>

      {rangeError && (
        <div
          role="alert"
          className="font-inter"
          style={{
            marginTop: '12px',
            fontSize: '13px',
            color: '#B91C1C',
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            padding: '8px 12px',
            borderRadius: '8px',
          }}
        >
          {rangeError}
        </div>
      )}

      {showTotals && <div style={{ marginTop: '20px' }}>{totalsCard}</div>}
      {truncatedNotice}

      {isMobile ? (
        <div style={{ marginTop: '20px' }}>
          <ListMobile
            language={language}
            columns={columns}
            rows={pageRows}
            rowKey={rowKey}
            loading={loading}
            count={tableData.length}
            page={page}
            limit={limit}
            onPageChange={setPage}
            sortBy={sortBy}
            sortDir={sortDir}
            onSortChange={handleSortChange}
            emptyState={inforTable}
          />
        </div>
      ) : (
        <div style={{ marginTop: '24px', flex: 1, minHeight: 0 }}>
          <div
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '14px',
              minHeight: 0,
            }}
          >
            <ListFilterBar
              language={language}
              columns={columns}
              visibleColumns={visibleColumns}
              onToggleColumn={(key: string) =>
                setVisibleColumns(prev => ({ ...prev, [key]: prev[key] === false }))
              }
              density={density}
              onDensityChange={setDensity}
              sortBy={sortBy}
              sortDir={sortDir}
              onSortChange={handleSortChange}
            />
            <div style={{ flex: 1, minHeight: 0 }}>
              <ListTable
                language={language}
                columns={columns}
                rows={pageRows}
                rowKey={rowKey}
                loading={loading}
                count={tableData.length}
                page={page}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={(next: number) => {
                  setLimit(next);
                  setPage(1);
                }}
                sortBy={sortBy}
                sortDir={sortDir}
                onSortChange={handleSortChange}
                emptyState={inforTable}
                visibleColumns={visibleColumns}
                density={density}
                totalsRow={totalsRow}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductPerformance;
